import { useCallback, useState } from "react";
import { useNavigate, useParams } from "react-router";
import _ from "lodash";

import { URLS } from "config/urls";
import theme from "theme";
import { colors } from "theme/palette";
import { downloadFileWithFileName } from "utils/file";
import { handleInvalidRequest } from "utils/sdk";

import Loading from "components/Loading";
import Paper from "components/Paper";
import Stack from "components/Stack";
import Text from "components/Text";
import toast from "components/Toast";
import { usePageTitle } from "components/usePageTitle";

import { useSignedUser } from "entities/Auth/sdk";
import {
  memoPDFSignedDocumentFileDataWithSignedUrlRetrieve,
  useMemoPDFWithSignedDocumentFile,
} from "entities/Memo/sdk";

const MemoESignatureProcessCompleted = () => {
  const params = useParams<{
    dealId: string;
    memoId: string;
  }>();
  const dealId = Number(params.dealId);
  const memoId = Number(params.memoId);

  const { data: snapshotData } = useMemoPDFWithSignedDocumentFile({
    dealId,
    memoId,
  });

  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const navigate = useNavigate();

  const { data: user } = useSignedUser();

  usePageTitle(
    snapshotData?.memo_name
      ? `Builders Patch: ${snapshotData.memo_name} - E-Sign Document`
      : "Builders Patch: E-Sign Document"
  );

  const onDownload = useCallback(() => {
    setIsDownloading(true);
    !_.isNil(snapshotData) &&
      memoPDFSignedDocumentFileDataWithSignedUrlRetrieve({
        memoId,
        dealId,
        memoPDFId: snapshotData.id,
      })
        .then((data) => {
          const toastId = toast.loading("Retrieving file...");

          downloadFileWithFileName({
            fileUrl: data.url,
            fileName: data.original_file_name,
            toastId,
          });
        })
        .catch(handleInvalidRequest)
        .finally(() => setIsDownloading(false));
  }, [dealId, memoId, snapshotData]);

  const navigateToLandingPage = useCallback(
    () => navigate(URLS.LANDING_PAGE),
    [navigate]
  );

  if (_.isNil(user)) {
    return <Loading open />;
  }

  return (
    <Paper
      sx={{
        padding: theme.spacing(0, 4, 1, 4),
        flex: 1,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Stack>
        <Stack
          padding={2}
          borderRadius="8px"
          gap={1.5}
          sx={{
            background: colors.blue10,
          }}
        >
          <Stack>
            <Text variant="label" fontWeight={500} color={colors.gray80}>
              e-sign document
            </Text>
            <Text variant="h2" data-testid="page-header-name">
              {snapshotData?.memo_name}
            </Text>
            <Text variant="text3" color={colors.gray80}>
              {user.company_name}
            </Text>
          </Stack>
          <Stack
            sx={{
              background: colors.blue20,
              width: "fit-content",
              padding: theme.spacing(0.25, 0.75),
              borderRadius: "4px",
            }}
          >
            <Text variant="text4" fontWeight={700}>
              {snapshotData?.deal_name}
            </Text>
          </Stack>
        </Stack>
      </Stack>
      <Stack position="relative" flex={1}>
        {!_.isNil(snapshotData) && (
          <Stack gap={2} marginTop={10}>
            <Text variant="h1">
              Document signed by all signatories. Download{" "}
              <Text
                component="span"
                variant="h1"
                color="primary"
                onClick={() => !isDownloading && onDownload()}
                sx={{ cursor: "pointer" }}
              >
                here.
              </Text>
            </Text>
            <Text variant="text2">
              You can return to the{" "}
              <Text
                onClick={navigateToLandingPage}
                component="span"
                variant="text2"
                color="primary"
                sx={{ cursor: "pointer" }}
              >
                homepage
              </Text>
              .
            </Text>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

export default MemoESignatureProcessCompleted;
